import { Column, EntityMetaProperty } from '@qtek/shared/models';
import { isNonNullable, isNullable } from '@qtek/shared/utils';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  MonoTypeOperatorFunction,
  Observable,
  of,
} from 'rxjs';
import { distinctUntilChanged, startWith } from 'rxjs/operators';
import { PsGroupByComponent } from '../../components';
import { transformerFactory } from '../transform';
import { summarizeGroup } from './grouping-utils';

export interface QtGroupByMeta<T> {
  entitySubject: BehaviorSubject<EntityMetaProperty | null>;
}

export interface ExtendDataTableByMeta {
  __groupBy: string;
  __groupById: string;
  __totalGroupCount: number;
  __groupId: string;
}

export function isExtendedDataTableByMeta<T>(
  data: any
): data is T & ExtendDataTableByMeta {
  return !!data?.__groupBy;
}

export function groupDataByMeta<T>(
  data: T[],
  groupBy: PsGroupByComponent,
  valueAccessor: (row: T, id: string, subId?: string, type?: string) => any,
  columns: Column[]
): T[] {
  const entitySubjectValue = groupBy.groupByMeta;

  if (!entitySubjectValue) {
    return data;
  }

  const value = (row: T, entitySubjectValue: EntityMetaProperty) => {
    return valueAccessor(
      row,
      `${entitySubjectValue.id}`,
      entitySubjectValue.subId
    );
  };

  const valueInArray = (
    row: T,
    entitySubjectValue: EntityMetaProperty,
    index: number
  ) => {
    return entitySubjectValue.subId
      ? valueAccessor(row, `${entitySubjectValue.id}`)[index][
          entitySubjectValue.subId
        ]
      : valueAccessor(row, `${entitySubjectValue.id}`)[index];
  };

  const reducedDataGroups = data.reduce((acc, curr) => {
    if (isNullable(entitySubjectValue)) {
      return acc;
    }

    const currentRow = valueAccessor(curr, entitySubjectValue.id);

    if (Array.isArray(currentRow)) {
      const arr: any[] = [];
      currentRow.forEach((_, index) => {
        const val = valueInArray(curr, entitySubjectValue, index);
        if (!acc.includes(val)) {
          arr.push(val);
        }
      });

      return [...acc, ...arr];
    }

    const currentValue = value(curr, entitySubjectValue);

    if (!acc.includes(currentValue)) {
      return [...acc, currentValue];
    }

    return acc;
  }, []);

  const sortedDataGroups = reducedDataGroups.sort((a, b) => {
    if (isNullable(a) && isNonNullable(b)) {
      return -1;
    } else if (isNullable(b) && isNonNullable(a)) {
      return 1;
    }

    return 0;
  });

  let groupedData: (T | (T & ExtendDataTableByMeta))[] = [];

  sortedDataGroups.forEach(dataGroup => {
    const inGroupElements: T[] = [];
    data.forEach(element => {
      if (isNullable(entitySubjectValue)) {
        return;
      }

      const currentRow = value(element, entitySubjectValue);

      if (Array.isArray(currentRow)) {
        if (entitySubjectValue.subId) {
          const found = currentRow.find(
            el => el[entitySubjectValue.subId] === dataGroup
          );
          if (found) {
            inGroupElements.push(element);
          }
        } else if (currentRow.includes(dataGroup) || currentRow === dataGroup) {
          inGroupElements.push(element);
        }
      } else if (!Array.isArray(currentRow) && currentRow === dataGroup) {
        inGroupElements.push(element);
      }
    });

    const groupSummarized = summarizeGroup(
      inGroupElements,
      valueAccessor,
      columns
    ) as T;

    // Carry the data.data shape of the first item over to the group row
    const firstItem = inGroupElements[0];
    const extractedData = {
      ...((firstItem as any)?.data?.data && {
        data: {
          data: { ...((groupSummarized as any)?.data?.data || {}) },
        },
      }),
    };

    const group: T & ExtendDataTableByMeta = {
      __groupBy: dataGroup,
      __groupById: entitySubjectValue.id,
      __totalGroupCount: inGroupElements.length,
      __groupId: 'groupId_' + dataGroup,
      ...groupSummarized,
      ...extractedData,
    };

    const groupElements: T[] = inGroupElements.map(element => {
      return { ...element, __groupBy: dataGroup } as T;
    });

    groupedData = [...groupedData, group, ...groupElements];
  });
  return groupedData as (T | (T & ExtendDataTableByMeta))[];
}

export function qtGroupByMeta<T>(
  groupBy: PsGroupByComponent,
  valueAccessor: (
    row: T | unknown,
    id: string,
    subId?: string,
    type?: string
  ) => unknown,
  columns: Column[] | Observable<Column[]> = []
): MonoTypeOperatorFunction<T[]> {
  if (!groupBy) {
    return transformerFactory<T, Column[]>(EMPTY, ([data]) => data);
  }

  const columns$: Observable<Column[]> = Array.isArray(columns)
    ? of(columns)
    : columns;

  const columnName$ = groupBy.selectedGroupBy$.pipe(distinctUntilChanged());

  const changes$ = combineLatest([
    columnName$.pipe(startWith(null)),
    columns$.pipe(startWith([])),
  ]);

  return transformerFactory<T, [EntityMetaProperty | null, Column[]] | null>(
    changes$,
    ([data, changesValue]) => {
      if (!changesValue || !Array.isArray(changesValue)) {
        return data;
      }
      const columnsValue = changesValue[1] ?? [];
      return groupDataByMeta<T>(data, groupBy, valueAccessor, columnsValue);
    }
  );
}
